import React, { useState, useEffect } from 'react';
import { Play, Pause, Music2, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { useAudio } from '../context/AudioContext';
import axios from 'axios';

const BACKEND_URL = '';

const getAudioUrl = (path) => {
  if (!path) return null;
  return path.startsWith('http') ? path : `${BACKEND_URL}${path}`;
};

const Discography = () => {
  const { currentTheme } = useTheme();
  const { playTrack, currentTrack, isPlaying, pause, play } = useAudio();
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedAlbums, setExpandedAlbums] = useState(new Set());
  const [descModal, setDescModal] = useState(null); // album object

  useEffect(() => {
    axios.get('/api/albums')
      .then(res => setAlbums(res.data))
      .catch(err => console.error('Failed to load albums:', err))
      .finally(() => setLoading(false));
  }, []);

  const buildPlaylist = (album) => {
    return album.tracks
      .filter(t => t.audioUrl)
      .map((t, i) => ({
        id: `${album.id}-track-${i}`,
        title: t.title,
        audioUrl: getAudioUrl(t.audioUrl),
        imageUrl: getAudioUrl(album.coverUrl),
        genre: album.title,
        bpm: null,
        duration: t.duration,
      }));
  };

  const toggleExpanded = (albumId) => {
    setExpandedAlbums(prev => {
      const next = new Set(prev);
      next.has(albumId) ? next.delete(albumId) : next.add(albumId);
      return next;
    });
  };

  const handlePlayAlbum = (album) => {
    const playlist = buildPlaylist(album);
    if (!playlist.length) return;
    const first = playlist[0];
    if (currentTrack?.id === first.id) {
      isPlaying ? pause() : play();
    } else {
      playTrack(first, playlist);
    }
    if (!expandedAlbums.has(album.id)) toggleExpanded(album.id);
  };

  const handlePlayTrack = (album, trackIndex) => {
    const playlist = buildPlaylist(album);
    if (!playlist[trackIndex]) return;
    playTrack(playlist[trackIndex], playlist);
  };

  const isAlbumPlaying = (album) => {
    if (!currentTrack || !isPlaying) return false;
    return album.tracks.some((_, i) => currentTrack.id === `${album.id}-track-${i}`);
  };

  const activeTrackId = currentTrack?.id;

  return (
    <div className="min-h-screen bg-black pt-24 pb-32">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Discography</h1>
          <p className="text-xl text-gray-400">Explore the complete catalog of projects and releases</p>
        </div>

        {loading && (
          <div className="text-center text-gray-400 py-20">Loading albums...</div>
        )}

        {/* Albums Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {albums.map(album => {
            const playlist = buildPlaylist(album);
            const playing = isAlbumPlaying(album);
            const expanded = expandedAlbums.has(album.id);

            return (
              <div
                key={album.id}
                className="bg-zinc-900 rounded-2xl overflow-hidden transition-all duration-300"
                style={{ boxShadow: playing ? `0 0 0 2px ${currentTheme.primary}` : 'none' }}
              >
                {/* Cover */}
                <div className="relative aspect-square overflow-hidden group cursor-pointer" onClick={() => handlePlayAlbum(album)}>
                  <img
                    src={getAudioUrl(album.coverUrl)}
                    alt={album.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-60" />

                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div
                      className="w-20 h-20 rounded-full flex items-center justify-center backdrop-blur-md bg-white/20 hover:scale-110 transition-transform"
                      style={{ border: `3px solid ${currentTheme.primary}` }}
                    >
                      {playing ? (
                        <Pause className="w-10 h-10" style={{ color: currentTheme.primary, fill: currentTheme.primary }} />
                      ) : (
                        <Play className="w-10 h-10 ml-1" style={{ color: currentTheme.primary, fill: currentTheme.primary }} />
                      )}
                    </div>
                  </div>

                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 rounded-full text-sm font-bold bg-black/50 backdrop-blur-sm text-white">
                      {album.year}
                    </span>
                  </div>

                  {playing && (
                    <div className="absolute bottom-4 left-4 flex items-center gap-1">
                      {[1,2,3].map(b => (
                        <div key={b} className="w-1 rounded-full animate-bounce" style={{ height: `${8 + b * 4}px`, backgroundColor: currentTheme.primary, animationDelay: `${b * 0.1}s` }} />
                      ))}
                      <span className="text-xs ml-2 font-bold" style={{ color: currentTheme.primary }}>PLAYING</span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-6">
                  <h3 className="text-white font-bold text-2xl mb-2">{album.title}</h3>
                  <div className="flex items-center gap-2 text-gray-400 mb-3">
                    <Music2 className="w-4 h-4" />
                    <span>{album.tracks?.length || 0} tracks</span>
                    {playlist.length < (album.tracks?.length || 0) && (
                      <span className="text-xs text-yellow-500">({playlist.length} with audio)</span>
                    )}
                  </div>
                  {album.description && (
                    <div className="relative mb-4 group/desc">
                      <p className="text-gray-400 text-sm line-clamp-2 leading-relaxed">{album.description}</p>
                      <button
                        onClick={() => setDescModal(album)}
                        className="mt-1 text-xs font-semibold transition-all hover:underline"
                        style={{ color: currentTheme.primary }}
                      >
                        Read the story →
                      </button>
                    </div>
                  )}

                  {/* Streaming Links */}
                  {album.streamingLinks && Object.keys(album.streamingLinks).length > 0 && (
                    <div className="flex gap-2 mb-4 flex-wrap">
                      {album.streamingLinks.spotify && (
                        <a href={album.streamingLinks.spotify} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-900/40 text-green-400 hover:bg-green-900/70 transition-all">
                          <ExternalLink className="w-3 h-3" /> Spotify
                        </a>
                      )}
                      {album.streamingLinks.appleMusic && (
                        <a href={album.streamingLinks.appleMusic} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-pink-900/40 text-pink-400 hover:bg-pink-900/70 transition-all">
                          <ExternalLink className="w-3 h-3" /> Apple Music
                        </a>
                      )}
                      {album.streamingLinks.youtube && (
                        <a href={album.streamingLinks.youtube} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-900/40 text-red-400 hover:bg-red-900/70 transition-all">
                          <ExternalLink className="w-3 h-3" /> YouTube
                        </a>
                      )}
                    </div>
                  )}

                  {/* Play + Tracklist toggle */}
                  <div className="flex gap-2">
                    <Button
                      className="flex-1 font-bold text-black hover:scale-105 transition-transform"
                      style={{ backgroundColor: currentTheme.primary }}
                      onClick={() => handlePlayAlbum(album)}
                      disabled={playlist.length === 0}
                    >
                      {playing ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                      {playing ? 'Pause' : playlist.length ? 'Play Album' : 'No Audio'}
                    </Button>
                    {album.tracks?.length > 0 && (
                      <Button
                        variant="outline"
                        className="text-gray-400 border-white/10 hover:bg-white/5"
                        onClick={() => toggleExpanded(album.id)}
                      >
                        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    )}
                  </div>

                  {/* Track List */}
                  {expanded && (
                    <div className="mt-4 space-y-1 border-t border-white/10 pt-4">
                      {album.tracks.map((track, idx) => {
                        const trackId = `${album.id}-track-${idx}`;
                        const isActive = activeTrackId === trackId;
                        const hasAudio = !!track.audioUrl;
                        return (
                          <div
                            key={idx}
                            onClick={() => hasAudio && handlePlayTrack(album, idx)}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${hasAudio ? 'cursor-pointer hover:bg-white/5' : 'opacity-40 cursor-not-allowed'} ${isActive ? 'bg-white/10' : ''}`}
                          >
                            <div className="w-6 text-center">
                              {isActive && isPlaying ? (
                                <div className="flex items-end gap-0.5 justify-center h-4">
                                  {[1,2,3].map(b => (
                                    <div key={b} className="w-0.5 rounded-full animate-bounce" style={{ height: `${4 + b * 3}px`, backgroundColor: currentTheme.primary, animationDelay: `${b * 0.1}s` }} />
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-500 text-xs">{idx + 1}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm truncate font-medium ${isActive ? 'text-white' : 'text-gray-300'}`} style={isActive ? { color: currentTheme.primary } : {}}>
                                {track.title}
                              </p>
                            </div>
                            <span className="text-gray-500 text-xs">{track.duration}</span>
                            {hasAudio && !isActive && (
                              <Play className="w-3 h-3 text-gray-600 group-hover:text-white" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Coming Soon */}
        {!loading && albums.length > 0 && (
          <div className="mt-16 p-12 rounded-2xl text-center"
            style={{ background: `linear-gradient(135deg, ${currentTheme.primary}10, transparent)` }}>
            <h2 className="text-3xl font-bold text-white mb-4">More Coming Soon</h2>
            <p className="text-gray-400 text-lg">Stay tuned for upcoming releases and exclusive content</p>
          </div>
        )}
      </div>

      {/* Description Modal */}
      {descModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
          onClick={() => setDescModal(null)}
        >
          <div
            className="relative max-w-2xl w-full rounded-3xl overflow-hidden shadow-2xl"
            style={{ background: 'linear-gradient(135deg, #111 60%, #1a1a1a)', border: `1px solid ${currentTheme.primary}40` }}
            onClick={e => e.stopPropagation()}
          >
            {/* Cover banner */}
            <div className="relative h-48 overflow-hidden">
              <img
                src={getAudioUrl(descModal.coverUrl)}
                alt={descModal.title}
                className="w-full h-full object-cover"
                style={{ filter: 'brightness(0.4)' }}
              />
              <div className="absolute inset-0 flex flex-col justify-end p-6">
                <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: currentTheme.primary }}>{descModal.year}</p>
                <h2 className="text-3xl font-bold text-white">{descModal.title}</h2>
                <p className="text-gray-400 text-sm mt-1">{descModal.tracks?.length} tracks</p>
              </div>
            </div>

            {/* Story */}
            <div className="p-8">
              <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: currentTheme.primary }}>The Story</p>
              <p className="text-gray-200 text-lg leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
                {descModal.description}
              </p>
            </div>

            {/* Close */}
            <button
              onClick={() => setDescModal(null)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/60 text-gray-400 hover:text-white flex items-center justify-center text-lg transition-all"
            >✕</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Discography;
